// ============================================================================
// CHA-240 — Carbon app shell nav config (chrome-only)
// ============================================================================
// Grouped navigation for the Carbon shell. Route IDs are IDENTICAL to the
// legacy `navItems` in InMoceanDashboard.jsx — only DISPLAY LABELS and the
// grouping are new (operator ratification #2: keep code ids `home`,
// `routing-beta`, etc.; change only what the user sees). Icons are the same
// lucide icons the legacy nav used per route.
import {
  Home,
  TrendingUp,
  Package,
  DollarSign,
  Boxes,
  Bot,
  ListChecks,
  AlertTriangle,
  BookOpen,
  Anchor,
  Truck,
  Navigation,
  Database,
  Settings,
  Archive,
} from 'lucide-react';

export const NAV_GROUPS = [
  {
    label: 'Operate',
    items: [
      { id: 'home', label: 'Today', icon: Home },
      { id: 'orders', label: 'Orders', icon: TrendingUp },
      { id: 'production', label: 'Production', icon: Package },
      { id: 'finance', label: 'Finance', icon: DollarSign },
      { id: 'inventory', label: 'Inventory', icon: Boxes },
    ],
  },
  {
    label: 'Intelligence',
    items: [
      { id: 'agents', label: 'Agents', icon: Bot },
      // CHA-588 (W1b) — the Work list. Placed beside Agents; inherits the Agents
      // page grant (below) so anyone who can open Agents can open Work.
      { id: 'work', label: 'Work', icon: ListChecks },
      // CHA-633 — Playbooks (the work layer's workflow definitions) is its own tab, admin-only. It replaces
      // the old "Reports & Workflows" item (the legacy InMoceanWorkflowSystem + reports), which is unmounted;
      // its #/workflow and #/reports hashes redirect to #/work (RETIRED_ROUTES below).
      { id: 'playbooks', label: 'Playbooks', icon: BookOpen },
      { id: 'alerts', label: 'Alerts', icon: AlertTriangle },
    ],
  },
  {
    label: 'Pipeline',
    items: [
      { id: 'imports', label: 'Imports', icon: Anchor },
      { id: 'logistics', label: 'Logistics', icon: Truck },
      { id: 'routing-beta', label: 'Routing', icon: Navigation },
      { id: 'data', label: 'Data', icon: Database },
      { id: 'admin', label: 'Admin', icon: Settings, adminOnly: true },
      // CHA-338 — parked legacy Actual Cost surface (pre-7/1/26). Admin-gated Archive area.
      { id: 'archive', label: 'Archive', icon: Archive, adminOnly: true },
    ],
  },
];

// ============================================================================
// CHA-553 (A+B) — page-grant → nav visibility
// ============================================================================
// The role editor grants PREFIXED page ids (`inmocean/orders`, `data/powerbi`)
// while the nav uses SHORT ids (`orders`, `data`). This map reconciles the two
// vocabularies: each nav id lists the grant ids that make it visible. Entries
// ending in `/` are PREFIX matches, so the `data` item shows for any `data/*`
// grant (e.g. the `powerbi` role's `data/powerbi`) without enumerating them.
//
// NOTE (scope): this is nav-level (UX) filtering only. Route rendering
// (InMoceanDashboard hash routes) and the callables behind each page are NOT
// yet gated — reaching a page by URL still works. Route + server-side
// enforcement are CHA-553 parts C/D. Hiding a nav item is not a security
// boundary; it is a usability affordance until those land.
export const NAV_PAGE_GRANTS = {
  home: ['inmocean/home'],
  orders: ['inmocean/orders'],
  production: ['inmocean/production'],
  finance: ['inmocean/finance'],
  inventory: ['inmocean/inventory'],
  agents: ['agents'],
  // CHA-588 (W1b) — Work reuses the Agents grant: any agents-granted role reaches
  // #/work (isRouteAllowed) and sees the Work nav item (visibleNavGroups).
  work: ['agents'],
  alerts: ['inmocean/alerts'],
  // CHA-633 — admin-only, matching canAdminTenant: the built-in admin and platform roles hold '*'. A custom
  // role holding the admin grant reaches the tab but sees an admins-only note (the page gates on role).
  playbooks: ['inmocean/admin'],
  imports: ['inmocean/imports', 'imports/'],
  logistics: ['inmocean/logistics'],
  'routing-beta': ['inmocean/routing-beta'],
  data: ['inmocean/data', 'data/'],
  admin: ['inmocean/admin'],
  archive: ['inmocean/admin'], // no distinct grant id; visible only to admin-granted roles
};

// CHA-633 — retired top-level routes and where they now land. The legacy "Reports & Workflows" surface is
// unmounted (its components stay in the repo); an old bookmark or link lands on Work instead of a blank page.
// Applied BEFORE the route guard, so the target is then checked against the role's grants as usual.
export const RETIRED_ROUTES = Object.freeze({ workflow: 'work', reports: 'work' });

/** The route to actually use: a retired route (by base segment) maps to its replacement. */
export function redirectRetiredRoute(route) {
  const base = (route || 'home').split('/')[0];
  // hasOwnProperty.call, not Object.hasOwn: this runs on every route change, and older Safari lacks hasOwn.
  return Object.prototype.hasOwnProperty.call(RETIRED_ROUTES, base) ? RETIRED_ROUTES[base] : route;
}

/**
 * Does a role holding `pages` reach the nav item `id`?
 * `pages` is the role's grant list. `'*'` (the admin wildcard) matches all.
 * @param {string} id  nav item id
 * @param {string[]} pages  role.pages
 * @returns {boolean}
 */
export function navItemVisible(id, pages) {
  if (!Array.isArray(pages)) return false;
  if (pages.includes('*')) return true;
  const grants = NAV_PAGE_GRANTS[id] || [];
  return grants.some((g) =>
    g.endsWith('/') ? pages.some((p) => p.startsWith(g)) : pages.includes(g)
  );
}

/**
 * Filter NAV_GROUPS to what a role holding `pages` may see.
 *
 * Transition policy (operator, CHA-553): ALLOW-BY-DEFAULT for MISSING data.
 * A role with no page list at all — `undefined`/`null`, which is what
 * `user`/`viewer` (no role document) resolve to — returns the full nav
 * unchanged, so existing accounts are never locked out.
 *
 * An explicit empty array `[]` is NOT missing data — it is a role an admin
 * deliberately created with zero pages, and it means zero access (matching
 * the canonical `userScope.hasPage`, which grants nothing for `[]`). So `[]`
 * yields an EMPTY nav, not the full one (CHA-553 / Codex): treating explicit
 * zero-access as allow-all would let an unchecked-everything role bypass the
 * restriction. Restriction otherwise applies to any non-empty grant list
 * (`powerbi`, `executive`, …); the admin wildcard `['*']` returns everything.
 *
 * @param {string[]|undefined|null} pages  role.pages
 * @returns {typeof NAV_GROUPS} groups with empty ones dropped
 */
export function visibleNavGroups(pages) {
  if (!Array.isArray(pages)) return NAV_GROUPS; // missing/legacy → allow-by-default
  if (pages.includes('*')) return NAV_GROUPS; // admin wildcard
  // Explicit array (INCLUDING []): filter. An empty array matches nothing and
  // yields an empty nav — an intentional zero-access role, not allow-all.
  return NAV_GROUPS
    .map((group) => ({ ...group, items: group.items.filter((it) => navItemVisible(it.id, pages)) }))
    .filter((group) => group.items.length > 0);
}

export function withNavLabelOverrides(groups, labelOverrides = {}) {
  if (!labelOverrides || Object.keys(labelOverrides).length === 0) return groups;
  return groups.map((group) => ({
    ...group,
    items: group.items.map((item) => ({
      ...item,
      label: labelOverrides[item.id] || item.label,
    })),
  }));
}

// ============================================================================
// CHA-553 (C, frontend) — landing route + top-level route guard
// ============================================================================
// Nav filtering (B) hides items a role can't reach; these close the gap the
// operator hit: a restricted role still LANDED on the hardcoded `home` route
// (Today) it has no grant for, and could still reach an un-granted top-level
// route by URL. Both key off the SAME grant list as visibleNavGroups.
//
// Granularity note: this is TOP-LEVEL (base-segment) enforcement. A route's
// base segment is its first path part (`data/analytics` -> `data`), matched
// against the nav item's grant. Sub-page enforcement WITHIN a granted section
// (e.g. a Data-granted role reaching only data/powerbi, not data/analytics)
// is finer and stays with the sub-page part of C/D — this does not claim it.
// Callable enforcement (D) is separate and unaffected.

/**
 * The route a role should land on: the id of the first nav item it can see.
 * Returns `home` when the role has no explicit grants (allow-by-default) or the
 * wildcard — both see everything, so `home` is correct. Returns `null` when an
 * EXPLICIT grant list yields no visible item (Ron's L3): a `pages: []`
 * zero-access role has NO valid landing route, so the caller must render a
 * no-access state, NOT fall back to `home` (which is exactly the bypass this
 * guard exists to close — #405 established `[]` means intentional zero access).
 * @param {string[]|undefined|null} pages
 * @returns {string|null} a route id, or null when the role may see nothing
 */
export function firstVisibleNavId(pages) {
  if (!Array.isArray(pages) || pages.includes('*')) return 'home';
  for (const group of NAV_GROUPS) {
    for (const item of group.items) {
      if (navItemVisible(item.id, pages)) return item.id;
    }
  }
  return null; // explicit list, nothing visible ([] zero-access) — no landing
}

/**
 * May a role holding `pages` see a specific PAGE (a sub-tab within a section,
 * e.g. `data/powerbi`)? Exact-match, same allow-by-default/wildcard policy as
 * the nav filter: missing pages (undefined — no role doc) and the `['*']`
 * wildcard grant everything; an explicit list grants only its exact members.
 * Used for sub-tab filtering (DataPage/ImportsPage) — finer than the
 * base-segment isRouteAllowed. Still a UX filter, not a callable boundary (D).
 * @param {string} pageId  e.g. 'data/powerbi'
 * @param {string[]|undefined|null} pages
 * @returns {boolean}
 */
export function isPageGranted(pageId, pages) {
  if (!Array.isArray(pages)) return true; // allow-by-default (no role doc)
  if (pages.includes('*')) return true; // admin
  return pages.includes(pageId);
}

/**
 * May a role holding `pages` view `route` at the TOP level? Matches the
 * route's base segment against the nav grants. Missing/wildcard pages allow
 * everything (allow-by-default / admin), consistent with visibleNavGroups.
 * @param {string} route  e.g. 'home', 'data/analytics'
 * @param {string[]|undefined|null} pages
 * @returns {boolean}
 */
export function isRouteAllowed(route, pages) {
  if (!Array.isArray(pages) || pages.includes('*')) return true;
  const base = (route || 'home').split('/')[0];
  return navItemVisible(base, pages);
}

// Active-match preserves the legacy behavior (InMoceanDashboard.jsx ~line 340):
// exact id OR a sub-route (`route.startsWith(id + '/')`), so `#/agents/yulie`
// keeps Agents active.
export const isRouteActive = (route, id) => {
  const r = route || '';
  return r === id || r.startsWith(id + '/');
};

// Route id -> display title for the top bar. Sub-routes resolve to their base
// segment's title (`agents/yulie` -> "Agents").
export const routeTitle = (route, labelOverrides = {}) => {
  const base = (route || 'home').split('/')[0];
  for (const group of NAV_GROUPS) {
    const found = group.items.find((it) => it.id === base);
    if (found) return labelOverrides[found.id] || found.label;
  }
  return 'Today';
};
